import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Input,
  Button,
  useToast,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Icon,
  HStack,
  Link,
} from '@chakra-ui/react';
import { Mail, CheckCircle } from 'lucide-react';
import { simpleWaitlistService } from '../../services/waitlist/simple-waitlist-service';
import { functions } from '../../services/firebase/firebase-config';
import { httpsCallable } from 'firebase/functions';
import { useSearchParams } from 'react-router-dom';

const SimpleWaitlistForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referredBy, setReferredBy] = useState<string | null>(null);
  const toast = useToast();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      setReferredBy(refParam);
    }
  }, [searchParams]);

  useEffect(() => {
    let isMounted = true;

    const loadWaitlistStats = async () => {
      try {
        const callable = httpsCallable(functions, 'getWaitlistStats');
        const result = await callable();
        if (!isMounted) return;
        const data = result.data as { totalCount?: number };
        setWaitlistCount(data?.totalCount ?? 0);
      } catch (statsError) {
        console.error('Error fetching waitlist stats:', statsError);
        if (isMounted) {
          setWaitlistCount(null);
        }
      }
    };

    loadWaitlistStats();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (waitlistCount === null) return;

    const interval = setInterval(() => {
      setWaitlistCount(prev => {
        if (prev === null) return prev;
        return prev + Math.floor(Math.random() * 2);
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [waitlistCount]);

  const generateReferralCode = (value: string) => {
    const base = value.split('@')[0] ?? 'coach';
    return `${base.replace(/[^a-z0-9]/gi, '').toLowerCase()}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const referral = generateReferralCode(email.trim().toLowerCase());

      await simpleWaitlistService.addToWaitlist(email, 'website', {
        referralCode: referral,
        referredBy: referredBy || null,
      });
      setIsSubmitted(true);
      setEmail('');
      setReferralCode(referral);
      
      toast({
        title: 'Success!',
        description: "You're on the list! We'll notify you when we launch.",
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      setError(error.message || 'Something went wrong. Please try again.');
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : 'https://coach-core-ai.web.app';
    const shareLink = referralCode
      ? `${origin}/waitlist?ref=${encodeURIComponent(referralCode)}`
      : `${origin}/waitlist`;

    const shareTargets = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        "I just joined the Coach Core AI waitlist!"
      )}&url=${encodeURIComponent(shareLink)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareLink
      )}`,
      email: `mailto:?subject=${encodeURIComponent(
        'Check out Coach Core AI'
      )}&body=${encodeURIComponent(
        `I just joined the Coach Core AI waitlist. Secure your spot here: ${shareLink}`
      )}`,
    };

    const copyReferralLink = async () => {
      try {
        await navigator.clipboard.writeText(shareLink);
        toast({
          title: 'Referral link copied! 🎉',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (clipboardError) {
        console.error('Failed to copy referral link:', clipboardError);
        toast({
          title: 'Copy failed',
          description: 'You can manually highlight and copy the link.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    };

    return (
      <Card maxW="md" mx="auto" bg="green.50" borderColor="green.200">
        <CardBody textAlign="center" py={8}>
          <Icon as={CheckCircle} boxSize={12} color="green.500" mb={4} />
          <Heading size="lg" color="green.700" mb={2}>
            You're on the list!
          </Heading>
          <Text color="green.600">
            We'll notify you as soon as Coach Core AI is ready.
          </Text>

          {referralCode && (
            <VStack spacing={4} mt={8} align="stretch">
              <Box
                borderWidth="1px"
                borderRadius="lg"
                bg="white"
                p={4}
                textAlign="left"
              >
                <Heading size="sm" mb={2} color="blue.600">
                  Share Coach Core AI with fellow coaches
                </Heading>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Every referral moves you up the early access list.
                </Text>
                <HStack spacing={2} mb={3} align="stretch">
                  <Input value={shareLink} readOnly fontSize="sm" />
                  <Button colorScheme="blue" onClick={copyReferralLink}>
                    Copy
                  </Button>
                </HStack>
                <HStack spacing={4} pt={1} flexWrap="wrap">
                  <Link
                    href={shareTargets.twitter}
                    isExternal
                    color="blue.500"
                    fontSize="sm"
                  >
                    Share on Twitter/X
                  </Link>
                  <Link
                    href={shareTargets.linkedin}
                    isExternal
                    color="blue.700"
                    fontSize="sm"
                  >
                    Share on LinkedIn
                  </Link>
                  <Link href={shareTargets.email} color="gray.600" fontSize="sm">
                    Email a friend
                  </Link>
                </HStack>
              </Box>
            </VStack>
          )}
        </CardBody>
      </Card>
    );
  }

  return (
    <Card maxW="md" mx="auto" shadow="xl">
      <CardHeader textAlign="center" pb={4}>
        <VStack spacing={3}>
          {waitlistCount !== null && (
            <Text fontSize="lg" color="blue.600" fontWeight="semibold">
              Join {waitlistCount}+ coaches already on the waitlist
            </Text>
          )}
          <Icon as={Mail} boxSize={8} color="blue.500" />
          <Heading size="lg" color="gray.800">
            Join the Waitlist
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Be the first to experience the future of coaching with AI
          </Text>
        </VStack>
      </CardHeader>
      
      <CardBody pt={0}>
        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isInvalid={!!error}>
              <FormLabel htmlFor="email" fontSize="sm" fontWeight="medium">
                Email Address
              </FormLabel>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="coach@example.com"
                size="lg"
                borderRadius="xl"
              />
              <FormErrorMessage>{error}</FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              width="full"
              borderRadius="xl"
              isLoading={isLoading}
              loadingText="Joining..."
              leftIcon={<Icon as={Mail} />}
            >
              Join Waitlist
            </Button>

            <Text fontSize="xs" color="gray.500" textAlign="center">
              We'll notify you when Coach Core AI launches.
            </Text>
          </VStack>
        </form>
      </CardBody>
    </Card>
  );
};

export default SimpleWaitlistForm;
